// T4-VIEWS-MEDIA: the board settings sheet's "Views" tab — the board's saved views, for whoever
// may edit the board. Per row, as hover icons: Make default (the view the board opens on) and
// Edit, a modal with the view's calendar date (calendar and timeline: due, start or any date
// field; dragging a task writes that date) and its card fields (the fields on its cards, gallery
// cards and calendar chips, in order). "Field defaults" leaves the view on the fields marked
// "On cards" in the task layout editor (Work OS's field-wide showOnCard).
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  Switch,
  type TagChipColor,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarMinusIcon,
  CalendarPlusIcon,
  PencilIcon,
  Share2Icon,
  StarIcon,
} from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../../api/client";
import type { CalendarView } from "../../../api/types";
import { ColorSwatches, ShareDialog } from "../../calendar/calendar-dialogs";
import { calendarKeys, calendarsQuery } from "../../calendar/calendar-queries";
import { problemText } from "../../settings/problem-text";
import { CARD_BUILTINS, dateFieldChoices, viewDateField } from "../card-fields";
import type { FieldDef, View } from "../view-engine";
import { VIEW_KINDS } from "../work-dialogs";
import { fieldsQuery, invalidateWork, viewsQuery, workKeys } from "../work-queries";
import { type BoardSettingsTabProps, registerBoardSettingsTab } from "./registry";

const kindLabel = (k: View["kind"]) => VIEW_KINDS.find((x) => x.value === k)?.label ?? k;
const DATED = new Set<View["kind"]>(["calendar", "timeline"]);

export function ViewsTab({ board, api, co }: BoardSettingsTabProps) {
  if (!api || !co) return null;
  return (
    <ViewsList
      api={api}
      co={co}
      boardId={board.id}
      boardName={board.name}
      archived={Boolean(board.archived_at)}
    />
  );
}

/** CAL-WORK-LAYERS: what a calendar view's row opens (publish, share, take out). */
type CalendarModal =
  | { kind: "publish"; view: View }
  | { kind: "share"; calendar: CalendarView }
  | { kind: "remove"; calendar: CalendarView };

function ViewsList({
  api,
  co,
  boardId,
  boardName,
  archived,
}: {
  api: Api;
  co: string;
  boardId: string;
  boardName: string;
  archived: boolean;
}) {
  const qc = useQueryClient();
  const views = useQuery(viewsQuery(api, co, boardId));
  const fields = useQuery(fieldsQuery(api, co, boardId));
  // The work calendars published from this board's views (the caller sees them: it views the
  // board).
  const cals = useQuery({ ...calendarsQuery(api, co), retry: false });
  const publishedOf = (v: View) =>
    (cals.data?.data ?? []).find((c) => c.kind === "work" && c.view_id === v.id) ?? null;
  const [calModal, setCalModal] = useState<CalendarModal | null>(null);
  const [editing, setEditing] = useState<View | null>(null);
  const [error, setError] = useState<string | null>(null);
  const unpublish = useMutation({
    mutationFn: (c: CalendarView) => api.client.calendars.unpublish({ params: { co, id: c.id } }),
    onMutate: () => setError(null),
    onError: (e) => setError(problemText(e, "The calendar could not be taken out.")),
    onSettled: () => qc.invalidateQueries({ queryKey: calendarKeys.root(co) }),
  });
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: workKeys.views(co, boardId) });
    await invalidateWork(qc, co);
  };
  const makeDefault = useMutation({
    mutationFn: (v: View) =>
      api.client.views.update({
        params: { co, id: boardId, sub_id: v.id },
        body: { is_default: true },
      }),
    onMutate: () => setError(null),
    onError: (e) => setError(problemText(e, "The default could not be changed.")),
    onSettled: refresh,
  });
  const rows = views.data?.data ?? [];
  const fieldList = fields.data?.data ?? [];
  return (
    <div data-tab="views" className="flex flex-col gap-2 text-sm">
      <p className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
        Saved views
        <InfoTip label="About views">
          Each view keeps its own date and card fields. A view on field defaults shows the fields
          marked “On cards”.
        </InfoTip>
      </p>
      {rows.length ? (
        <ul aria-label="Views" className="m-0 flex list-none flex-col p-0">
          {rows.map((v) => {
            const published = v.kind === "calendar" ? publishedOf(v) : null;
            return (
              <li
                key={v.id}
                data-view={v.id}
                className="group/view flex min-h-9 items-center gap-2 rounded-md px-1 hover:bg-muted"
              >
                <span className="min-w-0 flex-1 truncate">{v.name}</span>
                {published ? <Badge variant="success">In Calendar</Badge> : null}
                {v.is_default ? <Badge variant="info">Default</Badge> : null}
                <Badge variant="neutral">{kindLabel(v.kind)}</Badge>
                {archived ? null : (
                  <span className="flex items-center opacity-0 group-hover/view:opacity-100 group-focus-within/view:opacity-100 pointer-coarse:opacity-100">
                    {v.kind === "calendar" && !published ? (
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label={`Show ${v.name} in Calendar`}
                        title="Show in Calendar"
                        onClick={() => setCalModal({ kind: "publish", view: v })}
                      >
                        <CalendarPlusIcon aria-hidden="true" />
                      </Button>
                    ) : null}
                    {published?.manages ? (
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label={`Share ${published.name}`}
                        title="Share…"
                        onClick={() => setCalModal({ kind: "share", calendar: published })}
                      >
                        <Share2Icon aria-hidden="true" />
                      </Button>
                    ) : null}
                    {published ? (
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label={`Remove ${published.name} from Calendar`}
                        title="Remove from Calendar"
                        onClick={() => setCalModal({ kind: "remove", calendar: published })}
                      >
                        <CalendarMinusIcon aria-hidden="true" />
                      </Button>
                    ) : null}
                    {v.is_default ? null : (
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label={`Make ${v.name} the default`}
                        title="Make default"
                        onClick={() => makeDefault.mutate(v)}
                      >
                        <StarIcon aria-hidden="true" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label={`Edit ${v.name}`}
                      title="Edit"
                      onClick={() => setEditing(v)}
                    >
                      <PencilIcon aria-hidden="true" />
                    </Button>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      ) : views.isPending ? null : (
        <p className="text-muted-foreground">No saved views.</p>
      )}
      {error ? (
        <p role="alert" className="text-xs text-danger-text">
          {error}
        </p>
      ) : null}
      {editing ? (
        <ViewDialog
          api={api}
          co={co}
          boardId={boardId}
          view={editing}
          fields={fieldList}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      ) : null}
      {calModal?.kind === "publish" ? (
        <PublishDialog
          api={api}
          co={co}
          view={calModal.view}
          boardName={boardName}
          onClose={() => setCalModal(null)}
        />
      ) : null}
      {calModal?.kind === "share" ? (
        <ShareDialog
          api={api}
          company={{ id: co }}
          calendar={calModal.calendar}
          label={calModal.calendar.name}
          onClose={() => setCalModal(null)}
        />
      ) : null}
      {calModal?.kind === "remove" ? (
        <Dialog open onOpenChange={(o) => !o && setCalModal(null)}>
          <DialogContent className="sm:max-w-[380px]" data-slot="unpublish-dialog">
            <DialogHeader>
              <DialogTitle>Remove {calModal.calendar.name} from Calendar?</DialogTitle>
              <DialogDescription>
                The view stays on the board. People stop seeing it in Calendar.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="secondary" onClick={() => setCalModal(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  unpublish.mutate(calModal.calendar);
                  setCalModal(null);
                }}
              >
                Remove
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}

/** CAL-WORK-LAYERS: "Show in Calendar": a name and a colour, then everyone on the board sees it. */
function PublishDialog({
  api,
  co,
  view,
  boardName,
  onClose,
}: {
  api: Api;
  co: string;
  view: View;
  boardName: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const nameId = useId();
  const [name, setName] = useState(boardName);
  const [color, setColor] = useState<TagChipColor | null>(null);
  const publish = useMutation({
    mutationFn: () =>
      api.client.calendars.publish({
        params: { co },
        body: { view_id: view.id, name: name.trim() || boardName, color },
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: calendarKeys.root(co) });
      toast.add({ title: "In Calendar", description: name.trim() || boardName, type: "success" });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm" data-slot="publish-dialog">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            publish.mutate();
          }}
        >
          <DialogHeader>
            <span className="flex items-center gap-1.5 pr-8">
              <DialogTitle>Show in Calendar</DialogTitle>
              <InfoTip label="About work calendars">
                Everyone who can open the board sees it in Calendar. It never blocks anyone's time.
                Share it with others from its Share… icon.
              </InfoTip>
            </span>
            <DialogDescription>{view.name}</DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={nameId}>Name</FieldLabel>
            <Input
              id={nameId}
              value={name}
              maxLength={200}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel>Colour</FieldLabel>
            <ColorSwatches value={color} onPick={setColor} />
          </Field>
          {publish.isError ? (
            <p role="alert" className="text-xs text-danger-text">
              {problemText(publish.error, "The calendar could not be published.")}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={publish.isPending}>
              Show in Calendar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Every field a card can show: the built-ins, then the board's fields. */
function cardChoices(fields: readonly FieldDef[]) {
  return [
    ...CARD_BUILTINS.map((b) => ({ ref: b.ref, label: b.label })),
    ...fields.map((f) => ({ ref: `field:${f.id}`, label: f.name })),
  ];
}

export function ViewDialog({
  api,
  co,
  boardId,
  view,
  fields,
  onClose,
  onSaved,
}: {
  api: Api;
  co: string;
  boardId: string;
  view: View;
  fields: readonly FieldDef[];
  onClose: () => void;
  onSaved: () => Promise<unknown>;
}) {
  const ids = { date: useId(), cards: useId() };
  const dated = DATED.has(view.kind);
  const [date, setDate] = useState(viewDateField(view, fields));
  const [dateTouched, setDateTouched] = useState(false);
  const [own, setOwn] = useState(view.card_fields !== null);
  // In the saved order; a field picked here goes last (the cards show them in this order).
  const [picked, setPicked] = useState<string[]>(view.card_fields ?? []);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const choices = cardChoices(fields);
  const toggle = (ref: string, on: boolean) =>
    setPicked((p) => (on ? [...p.filter((x) => x !== ref), ref] : p.filter((x) => x !== ref)));
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      // Fields this person cannot see (another audience) stay in the list as they were.
      const cards = own ? picked : null;
      await api.client.views.update({
        params: { co, id: boardId, sub_id: view.id },
        body: { card_fields: cards, ...(dated && dateTouched ? { date_field: date } : {}) },
      });
      await onSaved();
      onClose();
    } catch (err) {
      setError(problemText(err, "The view could not be saved."));
    } finally {
      setPending(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm" data-slot="view-dialog">
        <form className="grid gap-4" onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Edit {view.name}</DialogTitle>
            <DialogDescription>Saved for everyone on this board.</DialogDescription>
          </DialogHeader>
          {dated ? (
            <Field>
              <FieldLabel htmlFor={ids.date} className="flex items-center gap-1">
                Date
                <InfoTip label="About the date">
                  Where tasks sit on the {kindLabel(view.kind).toLowerCase()}. Dragging a task
                  changes this date.
                </InfoTip>
              </FieldLabel>
              <NativeSelect
                id={ids.date}
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                  setDateTouched(true);
                }}
              >
                {dateFieldChoices(fields).map((c) => (
                  <NativeSelectOption key={c.ref} value={c.ref}>
                    {c.label}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
          ) : null}
          <Field>
            <FieldLabel id={ids.cards} className="flex items-center gap-1">
              Card fields
              <InfoTip label="About card fields">
                Off: the fields marked “On cards” in the task layout. On: pick this view's own.
              </InfoTip>
            </FieldLabel>
            <span className="flex items-center gap-2 text-sm">
              <Switch
                size="sm"
                aria-label="Own card fields"
                checked={own}
                onCheckedChange={(on) => setOwn(Boolean(on))}
              />
              {own ? "This view's own" : "Field defaults"}
            </span>
            {own ? (
              <ul
                aria-labelledby={ids.cards}
                data-slot="card-field-choices"
                className="m-0 grid list-none grid-cols-2 gap-x-3 gap-y-1.5 p-0"
              >
                {choices.map((c) => (
                  <li key={c.ref}>
                    {/* biome-ignore lint/a11y/noLabelWithoutControl: the Checkbox inside is the control */}
                    <label className="flex min-w-0 items-center gap-2 text-sm">
                      <Checkbox
                        checked={picked.includes(c.ref)}
                        onCheckedChange={(on) => toggle(c.ref, Boolean(on))}
                      />
                      <span className="truncate">{c.label}</span>
                    </label>
                  </li>
                ))}
              </ul>
            ) : null}
          </Field>
          {error ? (
            <p role="alert" className="text-xs text-danger-text">
              {error}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending || (own && picked.length > 12)}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const off = registerBoardSettingsTab({
  id: "views",
  title: "Views",
  order: 20,
  Component: ViewsTab,
  when: (p) => Boolean(p.api && p.co) && p.can.edit,
});
// A hot reload re-runs this module: the old registration goes first (no duplicate id).
import.meta.hot?.dispose(off);
